import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import ViewModuleIcon from '@mui/icons-material/ViewModuleOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import { topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import { usePortalCategoryColors } from '../portalColors';
import type { CurrentStockRow } from '@/types/reports';

const TOP_MATERIALS_COLORS = ['#0566d9', '#4edea3', '#ffb951', '#ba1a1a', '#9c6ade', '#3e5c8a', '#e0629e', '#2e9e8f'];

export default function InventoryPanel() {
  const { t } = useTranslation('clientPortal');
  const colors = usePortalCategoryColors();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['client-portal', 'inventory'],
    queryFn: async () => (await apiClient.get<CurrentStockRow[]>('/client-portal/inventory')).data,
  });

  const totalMaterials = useMemo(() => new Set(rows.map((r) => r.materialId)).size, [rows]);
  const totalOnHand = useMemo(() => rows.reduce((sum, r) => sum + r.quantityOnHand, 0), [rows]);
  const totalAllocated = useMemo(() => rows.reduce((sum, r) => sum + r.quantityAllocated, 0), [rows]);
  const totalAvailable = useMemo(() => rows.reduce((sum, r) => sum + r.quantityAvailable, 0), [rows]);

  const topMaterials = useMemo(() => topN(rows, (r) => r.quantityOnHand, 8), [rows]);

  const columns: DataTableColumn<CurrentStockRow>[] = [
    { field: 'materialCode', headerName: t('inventoryPage.table.materialCode') },
    { field: 'materialName', headerName: t('inventoryPage.table.materialName') },
    { field: 'warehouseName', headerName: t('inventoryPage.table.warehouseName') },
    { field: 'quantityOnHand', headerName: t('inventoryPage.table.quantityOnHand'), align: 'right' },
    { field: 'quantityAllocated', headerName: t('inventoryPage.table.quantityAllocated'), align: 'right' },
    { field: 'quantityAvailable', headerName: t('inventoryPage.table.quantityAvailable'), align: 'right' },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inventoryPanel.totalMaterials')} value={totalMaterials} icon={<Inventory2Icon />} accentColor={colors.inventory} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inventoryPanel.totalOnHand')} value={totalOnHand.toLocaleString()} icon={<ViewModuleIcon />} accentColor={colors.inventory} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inventoryPanel.totalAllocated')} value={totalAllocated.toLocaleString()} icon={<LockOutlinedIcon />} accentColor={colors.dispatch} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('inventoryPanel.totalAvailable')} value={totalAvailable.toLocaleString()} icon={<CheckCircleOutlineIcon />} accentColor={colors.overview} />
        </GridItem>
      </GridContainer>

      <ChartCard title={t('inventoryPanel.topMaterialsTitle')} loading={isLoading} empty={!isLoading && topMaterials.length === 0} height={320}>
        <DashboardBarChart
          layout="horizontal"
          categories={topMaterials.map((r) => r.materialCode)}
          series={[{ label: t('inventoryPage.table.quantityOnHand'), data: topMaterials.map((r) => r.quantityOnHand), color: colors.inventory }]}
          height={320}
          perItemColors={TOP_MATERIALS_COLORS}
          showValueLabels
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('inventoryPage.title')}
        </Typography>
        <DataTable
          columns={columns}
          rows={withId(rows, (r) => `${r.materialId}-${r.warehouseId}`)}
          loading={isLoading}
          searchable
          exportFileName="inventory"
        />
      </Paper>
    </Stack>
  );
}
